import React, { useEffect, useRef, useState } from 'react';
import { Group, Text, Rect } from 'react-konva';

export interface KonvaTextBoxProps {
  id: string;
  text: string;
  x: number;
  y: number;
  color: string;
  fontSize: number;
  bold: boolean;
  italic: boolean;
  underline?: boolean;
  isSelected?: boolean;
  onMove: (id: string, x: number, y: number) => void;
  onSelect?: (id: string) => void;
  stageScale: number;
}

const KonvaTextBox: React.FC<KonvaTextBoxProps> = ({
  id,
  text,
  x,
  y,
  color,
  fontSize,
  bold,
  italic,
  underline,
  isSelected = false,
  onMove,
  onSelect,
  stageScale,
}) => {
  const groupRef = useRef<any>(null);
  const [currentText, setCurrentText] = useState(text);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setCurrentText(text);
  }, [text]);

  useEffect(() => {
    if (editing) {
      const textarea = document.createElement('textarea');
      textarea.value = currentText;
      textarea.style.position = 'absolute';

      // Calculate absolute position within the viewport accounting for stage offset & scale
      const stage = groupRef.current?.getStage();
      if (stage) {
        const containerRect = stage.container().getBoundingClientRect();
        const absPos = groupRef.current.getAbsolutePosition();
        textarea.style.top = `${containerRect.top + absPos.y}px`;
        textarea.style.left = `${containerRect.left + absPos.x}px`;
      } else {
        // fallback – should not really happen
        textarea.style.top = `${y}px`;
        textarea.style.left = `${x}px`;
      }

      // dimensions & styling
      const estimatedWidth = Math.max(120, currentText.length * (fontSize / 1.5));
      const estimatedHeight = fontSize * 1.6;
      textarea.style.width = `${estimatedWidth * stageScale}px`;
      textarea.style.height = `${estimatedHeight * stageScale}px`;
      textarea.style.fontSize = `${fontSize / stageScale}px`;
      textarea.style.resize = 'both';
      textarea.style.border = '1px solid #d1d5db';
      textarea.style.borderRadius = '4px';
      textarea.style.background = 'transparent';
      textarea.style.outline = 'none';
      textarea.style.boxSizing = 'border-box';

      document.body.appendChild(textarea);
      textarea.focus();

      textarea.onblur = () => {
        setEditing(false);
        setCurrentText(textarea.value);
        document.body.removeChild(textarea);
      };
    }
  }, [editing]);

  const fontStyle = `${bold ? 'bold' : ''} ${italic ? 'italic' : ''}`.trim();
  const decoration = underline ? 'underline' : undefined;

  // measure text width for bounding box
  const estimatedWidth = Math.max(120, currentText.length * (fontSize / 1.5));
  const estimatedHeight = fontSize * 1.6;

  return (
    <Group
      x={x}
      y={y}
      draggable
      onDragEnd={(e) => onMove(id, e.target.x(), e.target.y())}
      onClick={() => onSelect && onSelect(id)}
      onDblClick={() => setEditing(true)}
      ref={groupRef}
    >
      {isSelected && (
        <Rect
          width={estimatedWidth}
          height={estimatedHeight}
          stroke="#3b82f6"
          strokeWidth={1}
          dash={[4, 2]}
        />
      )}
      <Text
        visible={!editing}
        text={currentText}
        fontSize={fontSize}
        fill={color}
        fontStyle={fontStyle}
        textDecoration={decoration}
      />
    </Group>
  );
};

export default KonvaTextBox; 